import { h, Component, Host, State, Element, Prop, Watch, Event, EventEmitter, Listen } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { TabsSchemeDefinition, TabsSchemeName } from "./constants/tabs-types";
import { TABS_SCHEME_CONFIG, TABS_SCHEME_MAP } from "./constants/tabs-constants";
import { KeyIds } from "../../constants/keys";
import { scrollToElement } from "../../utils/scroll-helper";
import { isMobile } from "../../utils/screen-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { detectSlotChanges } from "../../utils/slot-helper";
import { TabsSchemeConfiguration } from "../../constants/colors";

/**
 * @slot [unnamed] - Holds any number of eon-ui-tab elements. These are also slotted and hold any number of HTML elements.
 */
@Component({
  tag: "eon-ui-tabs",
  styleUrl: "tabs.scss",
  shadow: true
})
export class Tabs implements ColorableComponent<TabsSchemeConfiguration<TabsSchemeDefinition>> {
  schemeConfig: TabsSchemeConfiguration<TabsSchemeDefinition> = TABS_SCHEME_CONFIG;
  schemeMap = TABS_SCHEME_MAP;
  highlightBorder: any;
  tabHeadersContainer: any;
  tabHeaders: any;
  absoluteWrapper: any;
  initialLoad: boolean = true;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: TabsSchemeDefinition;
  @State() tabs: Element[] = [];
  @State() highlightedTabIndex: number = -1;

  @Element() hostElement: HTMLEonUiTabsElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: TabsSchemeName;

  /**
   * Sets this to a valid zero-based index of a tab to select it.
   */
  @Prop({ reflect: true, mutable: true }) activeTabIndex: number = 0;

  /**
   * Raised when the component has been loaded.
   */
  @Event() tabsLoad: EventEmitter;

  /**
   * Raised after the active tab has changed. Returns the index of the active tab.
   */
  @Event() tabChanged: EventEmitter<number>;

  componentWillLoad() {
    this.applyScheme();
    this.tabs = Array.from(this.hostElement.children);
    this.selectTab();

    document.addEventListener("click", (e) => {
      this.removeHighlightedTabIndex(e);
    });
  }

  componentDidLoad() {
    this.setCssVariables();
    this.tabsLoad.emit();
    detectSlotChanges(() => {
      this.tabs = Array.from(this.hostElement.children);
      this.selectTab();
      setTimeout(() => {
        this.setHtmlElements();
      }, 1000);
    }, this.hostElement);
    this.setHtmlElements();
    this.highlightActiveTabHeader();
  }

  disconnectedCallback() {
    document.removeEventListener("click", (e) => {
      this.removeHighlightedTabIndex(e);
    });
  }

  @Listen("resize", { target: "window" })
  onWindowResize() {
    if (isMobile()) this.highlightActiveTabHeader();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("activeTabIndex")
  selectTab() {
    if (this.activeTabIndex < 0 || this.activeTabIndex >= this.tabs.length) {
      return;
    }
    this.tabs.forEach((tab, index) => tab.setAttribute("is-active", (index === this.activeTabIndex).toString()));

    if (this.initialLoad) {
      this.initialLoad = false;
      return;
    }

    this.tabChanged.emit(this.activeTabIndex);
  }

  @Watch("activeTabIndex")
  highlightActiveTabHeader() {
    if (this.tabHeaders.length > 0) {
      let activeTabHeader = this.tabHeaders[this.activeTabIndex] as HTMLElement;
      // because the font is bold on active the width is more than calculated. That means we are wait for 100ms and than we calculate the width.
      setTimeout(() => {
        this.highlightBorder.setAttribute(
          "style",
          `width: ${activeTabHeader.offsetWidth}px; left: ${activeTabHeader.offsetLeft}px;`
        );
      }, 100);

      scrollToElement(activeTabHeader, {
        scrollContainer: this.absoluteWrapper,
        targetPosition: "center",
        direction: "horizontally",
        firstChild: this.tabHeaders[0]
      });
    }
  }

  @Watch("highlightedTabIndex")
  highlightTab() {
    if (this.highlightedTabIndex < 0 || this.highlightedTabIndex >= this.tabs.length) {
      return;
    }

    this.tabs.forEach((tab, index) =>
      tab.setAttribute("force-hover-state", (index === this.highlightedTabIndex).toString())
    );

    if (this.tabHeaders.length > 0) {
      let activeTabHeader = this.tabHeaders[this.highlightedTabIndex] as HTMLElement;

      scrollToElement(activeTabHeader, {
        scrollContainer: this.absoluteWrapper,
        targetPosition: "center",
        direction: "horizontally",
        firstChild: this.tabHeaders[0]
      });
    }
  }

  private setHtmlElements() {
    this.tabHeaders = this.tabHeadersContainer.querySelectorAll(`.${pfx("tab-header")}`);
    this.absoluteWrapper = this.tabHeadersContainer.querySelector(`.${pfx("absolute-wrapper")}`);
  }

  private setActiveTab(index: number) {
    this.activeTabIndex = index;
    this.highlightedTabIndex = index;
  }

  private handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.LEFT:
        const previousTabIndex = (this.highlightedTabIndex + this.tabs.length - 1) % this.tabs.length;
        this.tabHeaders[previousTabIndex]?.focus();
        this.setActiveTab(previousTabIndex);
        e.preventDefault();
        break;
      case KeyIds.RIGHT:
        // allow the first item to be highlighted when initially pressing RIGHT
        const nextTabIndex = this.highlightedTabIndex === -1 ? 0 : (this.highlightedTabIndex + 1) % this.tabs.length;
        this.tabHeaders[nextTabIndex]?.focus();
        this.setActiveTab(nextTabIndex);
        e.preventDefault();
        break;
      default:
        break;
    }
  }

  private removeHighlightedTabIndex(e) {
    const srcElement = e.srcElement as HTMLElement;
    if (srcElement.tagName != "EON-UI-TABS") {
      //in edge the tagName is not "EON-UI-DATE-PICKER" so we have to check if the hostelement contains the clicked element
      this.highlightedTabIndex = -1;
    }
  }

  render() {
    return (
      <Host>
        <div class={pfx("tab-headers")} ref={(el) => (this.tabHeadersContainer = el as HTMLElement)}>
          <div class={pfx("absolute-wrapper")}>
            <div class={pfx("highlight-border")} ref={(el) => (this.highlightBorder = el as HTMLElement)}></div>
            {this.tabs.map((tab: Element, index: number) => {
              return (
                <div
                  role="button"
                  tabIndex={index === this.activeTabIndex ? 0 : -1}
                  class={pfx({
                    "tab-header": true,
                    active: index === this.activeTabIndex,
                    forceHoverState: index === this.highlightedTabIndex
                  })}
                  onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
                  onClick={() => this.setActiveTab(index)}
                  innerHTML={tab.getAttribute("header")}
                  onFocus={() => this.setActiveTab(index)}
                ></div>
              );
            })}
          </div>
        </div>
        <div class={pfx("tab-panes")}>
          <slot />
        </div>
      </Host>
    );
  }
}
